import React, {
  ChangeEvent,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';

import { locale as localeConfig } from '@adsk/au-common-js';
import { Edit } from '@digital-hig/icon/icons/index';
import Alert from '@digital-hig/mui/Alert';
import Button from '@digital-hig/mui/Button';
import FormControl from '@digital-hig/mui/FormControl';
import FormControlLabel from '@digital-hig/mui/FormControlLabel';
import Input from '@digital-hig/mui/Input';
import InputLabel from '@digital-hig/mui/InputLabel';
import Radio from '@digital-hig/mui/Radio';
import RadioGroup from '@digital-hig/mui/RadioGroup';
import Select from '@digital-hig/mui/Select';
import Snackbar from '@digital-hig/mui/Snackbar';
import SvgIcon from '@digital-hig/mui/SvgIcon';
import Typography from '@digital-hig/mui/Typography';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import type { SelectChangeEvent } from '@mui/material';
import Slide, { SlideProps } from '@mui/material/Slide';
import LoadingOverlay from 'react-loading-overlay';

import PlaylistCard from './PlaylistCard';
import type { PlaylistCardProps } from './PlaylistCard';
import { generatePlaylistPathname } from './utils';
import type { CollectionEntryItem } from '../../../apis/collections/hooks';
import {
  useCollectionsList,
  usePlaylistController,
} from '../../../apis/collections/hooks';
import { useUserProfile } from '../../../apis/useUserSearch';
import User from '../../../components/container/user';
import Share from '../../../components/presentational/share/Share';
import extractErrorMessage from '../../../helpers/extractErrorMessage';
import languageOptions from '../../../helpers/languageOptions';
import type { ArticleResponse } from '../../../templates/article/type';
import { generateArticlePathname } from '../../../templates/article/utils';
import type { ClassResponse } from '../../../templates/class/type';
import { generateClassPathname } from '../../../templates/class/utils';
import {
  generateArticleEyebrow,
  generateClassEyebrow,
} from '../../../templates/search/utils';
import I18nContext from '../../contexts/I18nContext';
import LoginContext from '../../contexts/LoginContext';
import Bookmark from '../bookmark/Bookmark';

interface Props {
  langCode: string;
  baseURL?: string;
  playlistId?: string;
  userProfileURL?: string;
  userContentURL?: string;
}

function UsersCollections({
  playlistId = '',
  baseURL,
  userProfileURL,
  langCode,
}: Partial<Props> & {
  playlistId?: string;
  baseURL?: string;
}) {
  const { t } = useContext(I18nContext);

  const { playlistData } = usePlaylistController({
    baseURL,
    id: playlistId,
  });

  const { data: authorProfile } = useUserProfile({
    oxygenId: playlistData?.creator_id || '',
    enabled: !!playlistData?.creator_id,
    userProfileURL,
  });

  const { data: allUsersCollections } = useCollectionsList({
    baseURL,
    userId: playlistData?.creator_id || '',
    enabled: !!playlistData?.creator_id,
  });

  const publicList = allUsersCollections?.filter(
    collection => collection.is_public && playlistData?.id !== collection.id
  );

  if (!publicList || publicList.length === 0) return null;

  return (
    <div className="user-playlist">
      <h4 className="au-pl-c26 au-text-c24 au-leading-c36 au-font-element au-font normal au-border-l au-border-black au-mb-0">
        {t('playlists.listsNamePrefix', {
          ns: 'common',
          name: authorProfile?.firstName || '',
        })}
      </h4>
      <ul className="au-pt-c15 au-pb-c1 au-pl-c26 au-direction-ltr au-border-l au-border-gray-cc">
        {publicList.map((list, key) => {
          return (
            // eslint-disable-next-line react/no-array-index-key
            <li className="au-mb-c15" key={key}>
              <a
                href={generatePlaylistPathname(list.id, langCode)}
                className="au-color-black au-text-c16 au-leading-c28 hover:au-underline"
              >
                {list.title}
              </a>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
function Item({
  item,
  ...playlistCardProps
}: Props & Partial<PlaylistCardProps> & { item: CollectionEntryItem }) {
  const { collectionData, beehiveData } = item;
  const { langCode } = playlistCardProps;

  if (!collectionData || !beehiveData) {
    return null;
  }

  const { content_type, item_key, item_title } = collectionData;
  const { fields, sys } = beehiveData || {};

  let bookmark: ReactNode = null;
  let url = '';
  let eyebrow = content_type;
  const isArticle = content_type === 'article';
  if (isArticle) {
    url = generateArticlePathname(fields?.slug || '', langCode);
    eyebrow = generateArticleEyebrow(beehiveData as ArticleResponse);
    bookmark = (
      <Bookmark
        contentID={sys?.id || item_key}
        contentTitle={fields?.title || item_title}
        variant="icon"
        mode="light"
      />
    );
  }
  const isClass = content_type === 'class';
  if (isClass) {
    url = generateClassPathname(fields?.slug || '', langCode);
    eyebrow = generateClassEyebrow(beehiveData as ClassResponse);
    bookmark = (
      <Bookmark
        contentID={sys?.id || item_key}
        contentTitle={fields?.title || item_title}
        variant="icon"
        mode="light"
      />
    );
  }

  return (
    <PlaylistCard
      withFallbackImage
      cardId={sys?.id || item_key}
      url={url}
      title={fields?.title || item_title}
      header={eyebrow}
      image={fields?.overviewImage?.fields?.file?.url}
      bookmark={bookmark}
      {...playlistCardProps}
    />
  );
}

function Playlist({
  playlistId: receivedPlaylistId,
  langCode,
  baseURL,
  userProfileURL,
  userContentURL,
}: Props) {
  const { t, Trans } = useContext(I18nContext);
  const [playlistId, setPlaylistId] = useState(receivedPlaylistId || '');
  const [isEditMode, setIsEditMode] = useState(false);
  const { userProfile } = useContext(LoginContext);

  useEffect(() => {
    if (receivedPlaylistId) {
      setPlaylistId(receivedPlaylistId);
    }
  }, [receivedPlaylistId]);

  const {
    playlistData,
    isLoading,
    collectionItems: dataItems,
    reorderItems,
    savePlaylist,
    revertChanges,
    isUpdateError,
    updateError,
    updateErrorReset,
  } = usePlaylistController({
    baseURL,
    id: playlistId,
  });

  const showLoader = isLoading || playlistData?.isOptimistic;

  const [deletedItems, setDeletedItems] = useState<string[]>([]);

  const itemsForDisplay = dataItems.filter(item => {
    const itemId = item?.collectionData?.item_key;
    if (!itemId || !item.beehiveData) return false;
    return !deletedItems.includes(itemId);
  });

  const isAuthor =
    playlistData?.creator_id && playlistData?.creator_id === userProfile?.sub;

  useEffect(() => {
    if (receivedPlaylistId) return;

    const parsed: Record<string, string> = {};
    // eslint-disable-next-line array-callback-return
    const cleanHash = window.location.hash.split('?')[0];

    cleanHash
      .substring(1)
      .split('&')
      .forEach(item => {
        const arg = item.split('=');
        // eslint-disable-next-line prefer-destructuring
        parsed[arg[0]] = arg[1];
      });
    if (parsed.playlistid) {
      setPlaylistId(parsed.playlistid);
    }
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (typeof over?.id === 'undefined') return;
    reorderItems(active.id as string, over.id as string);
  };

  const [playlistTitle, setPlaylistTitle] = useState(playlistData?.title || '');
  const [playlistDescription, setPlaylistDescription] = useState(
    playlistData?.body || ''
  );
  const [playlistLanguage, setPlaylistLanguage] = useState(
    playlistData?.site_language || 'en'
  );
  const [playlistIsPublic, setPlaylistIsPublic] = useState<boolean>(
    !!playlistData?.is_public
  );

  useEffect(() => {
    if (playlistData?.title) {
      setPlaylistTitle(playlistData.title);
    }
  }, [playlistData?.title]);
  useEffect(() => {
    if (playlistData?.body) {
      setPlaylistDescription(playlistData.body);
    }
  }, [playlistData?.body]);
  useEffect(() => {
    if (playlistData?.site_language) {
      setPlaylistLanguage(playlistData.site_language);
    }
  }, [playlistData?.site_language]);
  useEffect(() => {
    if (playlistData) {
      setPlaylistIsPublic(Boolean(playlistData?.is_public));
    }
  }, [playlistData?.is_public]);

  const handleEditSave = async () => {
    setIsEditMode(false);

    let newTitle = '';
    let newBody = '';
    if (
      playlistTitle.trim().length > 0 &&
      playlistTitle !== playlistData?.title
    ) {
      newTitle = playlistTitle;
    }
    if (
      playlistDescription.trim().length > 0 &&
      playlistDescription !== playlistData?.body
    ) {
      newBody = playlistDescription;
    }

    await savePlaylist({
      ...(newTitle && { title: newTitle }),
      ...(newBody && { body: newBody }),
      deletedItems,
    });

    setDeletedItems([]);
  };

  const handleEditCancel = () => {
    revertChanges();
    setIsEditMode(false);
    setDeletedItems([]);
    setPlaylistTitle(playlistData?.title || '');
    setPlaylistDescription(playlistData?.body || '');
  };

  const itemsRendered = itemsForDisplay.map(item => (
    <Item
      langCode={langCode}
      item={item}
      key={item?.collectionData?.item_key}
      isEditMode={isEditMode}
      baseURL={baseURL}
      userProfileURL={userProfileURL}
      userContentURL={userContentURL}
      onDelete={async () => {
        const itemId = item?.collectionData?.item_key;
        if (!itemId || deletedItems.includes(itemId)) return;
        setDeletedItems([...deletedItems, itemId]);
      }}
    />
  ));

  const handleToastClose = () => {
    updateErrorReset();
  };

  return (
    <div className="au-bg-[#161616]">
      <LoadingOverlay
        active={showLoader}
        spinner
        fadeSpeed={250}
        styles={{
          overlay: (base: object) => ({
            ...base,
            position: 'fixed',
            zIndex: 94035,
          }),
        }}
      />
      <Snackbar
        open={isUpdateError}
        autoHideDuration={6000}
        onClose={handleToastClose}
        TransitionComponent={Slide as React.ComponentType<SlideProps>}
      >
        <Alert
          onClose={handleToastClose}
          severity="error"
          variant="filled"
          sx={{ margin: '8px 8px 16px', width: '100%' }}
        >
          {t('playlists.save_error', {
            ns: 'common',
            errorMessage: extractErrorMessage(updateError),
          })}
        </Alert>
      </Snackbar>
      <div className="au-bg-black au-pt-c95 au-pb-c65">
        <div className="au-container au-flex au-gap-c30">
          <div className="au-w-3/4">
            <h3 className="au-text-gray-cc au-font-element au-text-c14 au-leading-c21 au-uppercase au-font-normal au-mb-c10">
              {t('Playlist', { ns: 'common' })}
            </h3>
            <h1 className="au-text-white au-mb-c30 lg:au-text-c50 au-text-c35 au-font-bold">
              {isEditMode ? (
                <FormControl
                  fullWidth
                  error={playlistTitle.trim().length === 0}
                >
                  <Input
                    sx={{
                      backgroundColor: 'white',
                    }}
                    id={`playlist-title-${playlistData?.id}`}
                    multiline
                    rows={4}
                    value={playlistTitle}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      setPlaylistTitle(e.target.value);
                    }}
                  />
                </FormControl>
              ) : (
                playlistData?.title
              )}
            </h1>
            <User
              size="large"
              showPicture={false}
              showBio={false}
              hasLink
              bypassCache
              showFlyout
              oxygenId={playlistData?.creator_id || ''}
              userProfileURL={userProfileURL}
              userContentURL={userContentURL}
            />
          </div>
          <div className="au-w-1/4">
            <h4 className="au-text-c16 au-uppercase au-font-element au-font-regular au-mb-c10 au-text-white">
              {t('playlists.share_label', { ns: 'common' })}
            </h4>
            <Share
              title={playlistData?.title || ''}
              displayCopyURL
              networks={['email', 'linkedin', 'twitter', 'facebook']}
            />
          </div>
        </div>
      </div>
      <div className="au-py-c35 md:au-pt-c75">
        <div className="au-container au-flex au-justify-end">
          <div className="au-flex au-items-center au-gap-c20">
            {isEditMode && (
              <>
                <Button
                  onClick={handleEditSave}
                  disabled={
                    playlistDescription.trim().length === 0 ||
                    playlistTitle.trim().length === 0
                  }
                  className="!au-bg-[#FEFF09] !au-text-black"
                >
                  {t('playlists.save_label', { ns: 'common' })}
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleEditCancel}
                  className="!au-text-white !au-border-white"
                >
                  {t('Cancel', { ns: 'common' })}
                </Button>
              </>
            )}
            {!isEditMode && isAuthor && (
              <Button
                startIcon={
                  <SvgIcon fontSize="medium">
                    <Edit />
                  </SvgIcon>
                }
                className="!au-text-white"
                variant="link-button"
                onClick={() => setIsEditMode(true)}
              >
                {t('playlists.edit_label', { ns: 'common' })}
              </Button>
            )}
          </div>
        </div>
      </div>
      <div className="au-container au-text-white au-flex">
        <div className="lg:au-w-1/4 lg:au-pr-c35">
          <div className="au-flex au-flex-col au-gap-c35 au-py-c35 au-border-t au-border-gray-e5">
            <UsersCollections
              langCode={langCode}
              playlistId={playlistId}
              baseURL={baseURL}
              userProfileURL={userProfileURL}
              userContentURL={userContentURL}
            />
            {!!playlistData?.site_language && (
              <FormControl fullWidth>
                <InputLabel
                  htmlFor={`playlist-language-${playlistData?.id}`}
                  className="!au-text-white"
                >
                  {t('Language', { ns: 'common' })}
                </InputLabel>
                <Select
                  disabled={!isAuthor}
                  value={playlistLanguage}
                  displayEmpty
                  name={`playlist-language-${playlistData?.id}`}
                  id={`playlist-language-${playlistData?.id}`}
                  onChange={async (e: SelectChangeEvent) => {
                    setPlaylistLanguage(e.target.value);
                    savePlaylist({
                      site_language: e.target.value,
                    });
                  }}
                  options={languageOptions.map(item => ({
                    ...item,
                    children: t(`languages.${item.label}`, { ns: 'common' }),
                  }))}
                />
              </FormControl>
            )}
            {isAuthor && (
              <FormControl>
                <InputLabel id="playlist-is-public" className="!au-text-white">
                  {t('playlists.share_radio_label', { ns: 'common' })}
                </InputLabel>
                <RadioGroup
                  aria-labelledby="playlist-is-public"
                  value={playlistIsPublic ? 'everyone' : 'link_only'}
                  name="playlist-is-public"
                  sx={{ gap: 4 }}
                  onChange={async (event: React.ChangeEvent, value: string) => {
                    setPlaylistIsPublic(value === 'everyone');
                    savePlaylist({
                      is_public: value === 'everyone',
                    });
                  }}
                >
                  <FormControlLabel
                    sx={{ alignItems: 'flex-start' }}
                    control={
                      <Radio
                        sx={{
                          mr: 2,
                          my: 0,
                          backgroundColor: 'white',
                          '&.Mui-checked': { backgroundColor: 'white' },
                        }}
                      />
                    }
                    value="link_only"
                    label={
                      <Typography
                        variant="body-copy-medium"
                        className="!au-text-white"
                      >
                        <Trans i18nKey="playlists.shareLinkOnly" ns="common">
                          <strong>Only people with the link.</strong> Your
                          playlist will not come up in search results or on your
                          profile. Only those who know the link can view it, and
                          you can share the link with anyone, even those who are
                          not signed in.
                        </Trans>
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    sx={{ alignItems: 'flex-start' }}
                    control={
                      <Radio
                        sx={{
                          mr: 2,
                          my: 0,
                          backgroundColor: 'white',
                          '&.Mui-checked': { backgroundColor: 'white' },
                        }}
                      />
                    }
                    value="everyone"
                    label={
                      <Typography
                        variant="body-copy-medium"
                        className="!au-text-white"
                      >
                        <Trans i18nKey="playlists.shareEveryone" ns="common">
                          <strong>Anyone.</strong> Other website users can
                          discover your playlist on the AU website in places
                          like search results, your profile, or the home page.
                        </Trans>
                      </Typography>
                    }
                  />
                </RadioGroup>
              </FormControl>
            )}
          </div>
        </div>
        <div className="lg:au-w-3/4">
          <h1 className="au-mb-c10">{t('Description', { ns: 'common' })}</h1>
          {isEditMode ? (
            <FormControl
              fullWidth
              sx={{ backgroundColor: 'white', margin: '0 0 50px 0' }}
              error={playlistDescription.trim().length === 0}
            >
              <Input
                id={`playlist-description-${playlistData?.id}`}
                multiline
                rows={4}
                value={playlistDescription}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  setPlaylistDescription(e.target.value);
                }}
              />
            </FormControl>
          ) : (
            <p className="au-mb-c50">{playlistData?.body}</p>
          )}
          <h3 className="au-font-element au-text-c34 au-font-bold au-leading-c47 au-mb-0">
            {t('{{count}} items', {
              ns: 'common',
              count: itemsForDisplay?.length || 0,
            })}
          </h3>
          <div className="au-border-t au-border-gray-e5 au-pt-c45 au-mt-c10 au-mb-c100 au-flex au-flex-col au-gap-c30">
            {isEditMode ? (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={
                    itemsForDisplay.map(
                      item => item?.collectionData?.item_key || ''
                    ) || []
                  }
                  strategy={verticalListSortingStrategy}
                >
                  {itemsRendered}
                </SortableContext>
              </DndContext>
            ) : (
              itemsRendered
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Shared attribute definition for Block Editor and AURR.
Playlist.attributes = {
  baseURL: {
    type: 'string',
    default: 'https://api.au-uw2-prd.autodesk.com',
  },
  taxonomyTermId: {
    type: 'string',
    default: '',
  },
  industry: {
    type: 'string',
    default: '',
  },
  topic: {
    type: 'string',
    default: '',
  },
  product: {
    type: 'string',
    default: '',
  },
  langCode: {
    type: 'string',
    enum: localeConfig.urlLocales,
    default: localeConfig.defaultURLLocale,
    list: languageOptions,
  },
  max: {
    type: 'integer',
    default: 3,
  },
  largeCardIdx: {
    type: 'integer',
    default: 1,
  },
  displayMsgCard: {
    type: 'boolean',
    default: false,
  },
  msgCard: {
    type: 'object',
    default: {
      idx: 0,
      header: '',
      title: '',
      footer: '',
    },
  },
};

export default Playlist;
